import React, { PropTypes, PureComponent } from 'react';
import update from 'immutability-helper';
import sport from '@letsdothis/reference-data/src/sport.json';
import { connect } from 'react-redux';

import { searches } from '../../utils/localStorage';
import { getLocalStorage, setLocalStorage } from '../../utils';

import EventSearchFilters from '../../components/EventSearchFilters';

class EventSearchFiltersContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      conversational: false,
      editing: '',
      fetching: false,
      isOpen: false,
      values: props.values,
    };
  }

  componentDidMount() {
    this.fetchData();

    const { conversational, isOpen } = this.props;
    // If filters should be open by default:
    if (isOpen) {
      this.toggleOpen();
    }
    // If is in 'I am a runner looking for...' style:
    if (conversational) {
      this.toggleLabels();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { values } = this.props;
    if (values.sport !== nextProps.values.sport ||
      values.sportDistance !== nextProps.values.sportDistance ||
      values.courseDetail !== nextProps.values.courseDetail ||
      values.location !== nextProps.values.location) {
      this.setState({ values: nextProps.values });
    }
  }

  componentDidUpdate() {
    this.updateExternalValues();
    if (this.props.onToggle) this.props.onToggle(!!this.state.editing);
  }

  setDefaultSelections = () => {
    // If default selection is needed, set to Running/HalfMarathons/London.
    const lastSearch = JSON.parse(getLocalStorage(searches.last.key));
    if (lastSearch) {
      this.setState(state =>
        update(state, {
          values: {
            sport: { $set: lastSearch.sport },
            sportDistance: { $set: lastSearch.sportDistance },
            geolocation: { $set: false },
            ...(lastSearch.locationName && {
              location: {
                $set: {
                  name: lastSearch.locationName,
                  ...(lastSearch.locationLat ? {
                    types: ['locality', 'political'],
                    geometry: {
                      location: {
                        lat: () => lastSearch.locationLat,
                        lng: () => lastSearch.locationLong,
                      },
                    },
                  } : {
                    types: ['bounds'],
                    geometry: {
                      viewport: {
                        getNorthEast: () => ({
                          lat: () => lastSearch.viewportNorthEastLat,
                          lng: () => lastSearch.viewportNorthEastLong,
                        }),
                        getSouthWest: () => ({
                          lat: () => lastSearch.viewportSouthWestLat,
                          lng: () => lastSearch.viewportSouthWestLong,
                        }),
                      },
                    },
                  }),
                },
              },
              locationSearchRadius: { $set: lastSearch.locationSearchRadius },
            }),
          },
        }),
      );
    } else {
      this.setState(state =>
        update(state, {
          values: {
            sport: { $set: 3 },
            sportDistance: { $set: [170] },
          },
        }),
      );
    }
  };

  fetchData = () => {
    const data = sport;
    this.setState({
      // Reduce sports to ID label pairs
      // sports: data.map(({ id, label }) => ({ id, label })),
      sports: data
        .filter(({ id }) => id !== 86)
        .map(({ id, label, noun }) => ({ id, label, noun })),
      // Reduce distances to flat array of ID label pairs with parent ID
      distances:
        data.filter(({ distances }) => distances.length)
          .map(({ id: parent, distances }) =>
            distances.map(({ id, label }) => ({ id, label, parent })))
          .reduce((flatten, distances) => flatten.concat(distances), []),
      courseDetails:
        data.filter(({ courseDetails }) => courseDetails && courseDetails.length)
          .map(({ id: parent, courseDetails }) =>
            courseDetails.map(({ id, label }) => ({ id, label, parent })))
          .reduce((flatten, courseDetails) => flatten.concat(courseDetails), []),
    });
  }

  updateExternalValues = () => {
    // If an external onChange function is passed into container component
    // then send this.state.values to external func (e.g. from HeaderSearch)
    const { onChange } = this.props;
    if (onChange) {
      onChange(this.state.values);
    }
  }

  handleChange = (event) => {
    // Delegate field change events
    // Reducer style, this might later end up in Redux store
    switch (event.target.name) {
      case 'geolocation':
        this.handleChangeGeolocation(event);
        break;
      case 'location':
      case 'locationSearchRadius':
        this.handleChangeLocation(event);
        break;
      case 'maxDate':
      case 'minDate':
        this.handleChangeDate(event);
        break;
      case 'sport':
        this.handleChangeSport(event);
        break;
      default:
        // Catch dynamically named fields e.g. sportDistance-1
        this.handleChangeCheckbox(event);
        break;
    }
  }

  handleChangeCheckbox = (event) => {
    const { target } = event;
    const [name, id] = target.name.split('-');
    const value = parseInt(id, 10);
    if (target.checked) {
      // Push ID into array of IDs
      this.setState(state =>
        update(state, {
          values: {
            [name]: { $set: (state.values[name] || []).concat([value]) },
          },
        }),
      );
    } else {
      // Splice ID out of array of IDs
      this.setState(state =>
        update(state, {
          values: {
            [name]: { $splice: [[state.values[name].indexOf(value), 1]] },
          },
        }),
      );
    }
  }

  handleChangeGeolocation = (event) => {
    const { value } = event.target;
    this.setState(state =>
      update(state, {
        values: {
          geolocation: { $set: true },
          location: { $set: value },
          // ..and set default proximity unless already set
          ...(!state.values.locationSearchRadius)
            && { locationSearchRadius: { $set: 50 } },
        },
      }));
  };

  handleChangeLocation = (event) => {
    // Handle location place and proximity
    const { name, value } = event.target;
    const { countryCode } = this.props;
    this.setState(state =>
      update(state,
        name === 'location'
          ? {
            values: {
              // Reset geolocation if we're using a place instead
              geolocation: { $set: false },
              [name]: { $set: value },
              // ..and set default proximity unless already set
              ...(!state.values.locationSearchRadius && value)
                && { locationSearchRadius: { $set: countryCode === 'US' ? 200 : 50 } },
            },
          }
          : {
            values: {
              [name]: { $set: parseInt(value, 10) },
            },
          },
      ));
  };

  handleChangeDate = (event) => {
    // Store simple name and primative
    const { name, value } = event.target;
    this.setState(state =>
      update(state, {
        values: {
          [name]: { $set: value },
        },
      }));
  };

  handleChangeSport = (event) => {
    const value = parseInt(event.target.value, 10);
    this.setState(state =>
      update(state, {
        // Set new sport and nuke sport-related choices
        values: {
          sport: { $set: value },
          sportDistance: { $set: undefined },
          courseDetail: { $set: undefined },
        },
      }));
  }

  handleEdit = (event) => {
    if (this.state.editing && (this.state.editing !== event.target.name)) {
      this.handleSubmit();
    }
    this.setState({
      editing: event.target.name,
    });
  }

  handleFetched = () => {
    this.setState({ fetching: false });
  }

  handleFetching = () => {
    this.setState({ fetching: true });
  }

  handleSubmit = () => {
    if (this.state.fetching) {
      //
      return;
    }
    this.setState({
      editing: undefined,
      ...!this.state.conversational && {
        isOpen: false,
      },
    });
    // Save search to local storage
    const values = this.cleanValues(this.state.values);
    setLocalStorage(searches.last.key, JSON.stringify(values));
    this.props.onSubmit(values);
  }

  handleToggleClick = () => {
    this.toggleOpen();
  }

  toggleOpen = () => {
    this.setState(({ isOpen }) => ({
      isOpen: !isOpen,
    }));
  }

  toggleLabels = () => {
    this.setState(({ conversational }) => ({
      conversational: !conversational,
    }), this.setDefaultSelections);
  }

  cleanValues = values =>
    // The values object represents the current state of selections,
    // not the computed values to be submitted, this function determines those choices
    Object.keys(values)
      // Fields to remove, e.g. slugs (using ID instead), non-search params e.g. geolocation
      .filter(key => ![
        'locationSlug',
        'geolocation',
        'locationName',
        'locationLat',
        'locationLong',
        'viewportNorthEastLat',
        'viewportNorthEastLong',
        'viewportSouthWestLat',
        'viewportSouthWestLong'].includes(key))
      .reduce((obj, key) => {
        // Location is a complex object, we only need the lat and long params
        if (key === 'location' && values[key]) {
          const place = values[key];
          // These location types have bounds search
          if (['country', 'administrative_area_level_1', 'administrative_area_level_2', 'bounds'].includes(place.types[0])) {
            return {
              ...obj,
              locationName: place.name,
              viewportNorthEastLat: place.geometry.viewport.getNorthEast().lat(),
              viewportNorthEastLong: place.geometry.viewport.getNorthEast().lng(),
              viewportSouthWestLat: place.geometry.viewport.getSouthWest().lat(),
              viewportSouthWestLong: place.geometry.viewport.getSouthWest().lng(),
            };
          }
          // Otherwise use a single-point search
          return {
            ...obj,
            locationName: place.name,
            locationLat: place.geometry.location.lat(),
            locationLong: place.geometry.location.lng(),
          };
        }
        // Radius is only valid for single-point queries
        if (key === 'locationSearchRadius'
            && (['country', 'administrative_area_level_1', 'administrative_area_level_2'].includes(values.location && values.location.types[0]) || !values.location)) {
          return {
            ...obj,
            [key]: undefined,
          };
        }
        // Everything else is fine, but nullify empty strings for brevity
        return {
          ...obj,
          [key]: values[key] === '' ? null : values[key],
        };
      }, {});

  render() {
    const {
      courseDetails,
      distances,
      sports,
      values: {
        sport: selectedSport,
      },
    } = this.state;

    if (!sports || !distances || !courseDetails) {
      // TODO: Ensure these are present with pre-fetch
      return null;
    }

    // Distances are conditional based on current sport choice
    const filteredDistances = selectedSport
      ? distances.filter(distance => distance.parent === selectedSport)
      : [];

    const filteredCourseDetails = selectedSport
      ? courseDetails.filter(courseDetail => courseDetail.parent === selectedSport)
      : [];

    return (
      <EventSearchFilters
        {...this.props}
        {...this.state}
        onChange={this.handleChange}
        onEdit={this.handleEdit}
        onFetched={this.handleFetched}
        onFetching={this.handleFetching}
        onSubmit={this.handleSubmit}
        onToggleClick={this.handleToggleClick}
        distances={filteredDistances.length ? filteredDistances : undefined}
        courseDetails={filteredCourseDetails.length ? filteredCourseDetails : undefined}
      />
    );
  }
}

EventSearchFiltersContainer.propTypes = {
  /**
   * Function called on form submit.
   */
  onSubmit: PropTypes.func,
  /**
   * Object to populate form selections.
   */
  values: PropTypes.shape(),
  /**
   * Bool to add conversation tone, i.e. `I am a X looking for Y near Z`.
   */
  conversational: PropTypes.bool,
  /**
   * Bool to determine whether filters should be open by default.
   */
  isOpen: PropTypes.bool,
  /**
   * Function to Update external values (if prop exists).
   */
  onChange: PropTypes.func,
  /**
   * The code representing the country in which the session is taking place
   */
  countryCode: PropTypes.string,
};

EventSearchFiltersContainer.defaultProps = {
  conversational: false,
  countryCode: undefined,
  isOpen: false,
  onChange: () => {},
  onSubmit: () => {},
  values: {},
};

const mapStateToProps = state => ({
  countryCode: state.countryCode,
});

const EventSearchFiltersWithRedux = connect(mapStateToProps)(EventSearchFiltersContainer); // eslint-disable-line no-class-assign, max-len

export default EventSearchFiltersWithRedux;



// WEBPACK FOOTER //
// ./app/src/containers/EventSearchFilters/index.jsx